import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { AppState } from '../../../store';
import { onGetAllProducts } from '../../../store/actions/productActions';
import LinearProgressBar from '../../../common/LinearProgress';
import { dateFormatToLocalString } from '../../../utils';
import { showErrorMessage, showSuccessMessage } from '../../../common/Toaster';
import jwtAxios from '../../../services/axiosInstance/ApiConfig';
import { getHeaders } from '../../../store/actions/Auth';
import { listOrders } from '../../../store/actions/orderActions';
import { ORDER_LIST_SUCCESS } from '../../../constants/orderConstants';

interface Column {
	id: 'id' | 'name' | 'description' | 'image' | 'brand' | 'category' | 'subcategory' | 'price' | 'countInStock' | 'rating' | 'numReviews' | 'created' | 'updated' | 'actions';
	label: string;
	minWidth?: number;
	align?: 'left' | 'right' | 'center';
	format?: (value: number) => string;
}

const columns: readonly Column[] = [
	{ id: 'id', label: 'Product Id', minWidth: 50 },
	{ id: 'name', label: 'Name', minWidth: 170 },
    { id: 'description', label: 'Description', minWidth: 170 },
	{ id: 'image', label: 'Image Url', minWidth: 170 },
	{ id: 'brand', label: 'Brand', minWidth: 170 },
	{ id: 'category', label: 'Category', minWidth: 170 },
	{ id: 'subcategory', label: 'Sub Category', minWidth: 170 },
    { id: 'price', label: 'Price', minWidth: 170 },
	{ id: 'countInStock', label: 'Count in stock', minWidth: 170 },
    { id: 'rating', label: 'Rating', minWidth: 170 },
	{ id: 'numReviews', label: 'Total reviews', minWidth: 170 },
    { id: 'created', label: 'Created date', minWidth: 170 },
    { id: 'updated', label: 'Updated date', minWidth: 170 },
	{ id: 'actions', label: 'Actions', minWidth: 170, align: 'center' },
];

interface RowType {
	id: string;
	name: string;
    description: string;
	image: string;
	brand: string;
	category: string;
	subcategory: string;
    price: string;
	countInStock: number;
    rating: number;
	numReviews: number;
    created: string;
    updated: string;
	actions: string;
}

function createRows(
	id: string,
	name: string,
    description: string,
	image: string,
	brand: string,
	category: string,
	subcategory: string,
    price: string,
    countInStock: number,
    rating: number,
	numReviews: number,
    created: string,
    updated: string,
	actions: string,
): RowType {
	return { id, name, description, image, brand, category, subcategory, price, countInStock, rating, numReviews, created, updated, actions };
}

interface OrderListsProps {
	handleSelectedOrder: (order: any) => void
}

export default function OrderLists(props: OrderListsProps) {
	const dispatch = useDispatch()
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(10);

	const {orders}: any = useSelector<AppState, AppState['orderList']>(
        ({orderList}) => orderList,
    );

	const {loading} = useSelector<AppState, AppState['common']>(
        ({common}) => common,
    );

	useEffect(() => {
		if(orders && orders.length > 0) {
			return 
		} else {
			dispatch({ type: ORDER_LIST_SUCCESS, payload: null });
			dispatch(listOrders({}))
		}
	}, [dispatch, orders])

	const handleChangePage = (event: unknown, newPage: number) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
		setRowsPerPage(+event.target.value);
		setPage(0);
	};

	const handleDeleteProduct = (rowId: string) => {
		jwtAxios.delete('/products/deleteProduct/' + rowId, { headers: getHeaders()})
		.then(response => {
			showSuccessMessage(response.data.message)
			dispatch(onGetAllProducts({}))
		}).catch((err: any) => {
			showErrorMessage(err)
		})
	}

	const rows: any =
		orders && orders &&
		orders.length > 0 
		? orders.map((product: any) => createRows(
			product._id,
			product.name,
			product.description,
			product.image,
			product.brand,
			product.category,
			product.subcategory,
			product.price,
			product.countInStock,
			product.rating,
			product.numReviews,
			product.createdAt,
			product.updatedAt,
			product.actions
		)) 
		: [];

	return (
		<Paper sx={{ width: '100%', overflow: 'hidden', padding: 0 }}>
			<TableContainer>
				{loading && <LinearProgressBar />}
				<Table stickyHeader aria-label="sticky table">
					<TableHead>
						<TableRow>
							{columns.map((column) => (
								<TableCell
									key={column.id}
									align={column.align}
									style={{ minWidth: column.minWidth, fontWeight: 600 }}
								>
									{column.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows
							.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
							.map((row: RowType) => {
								return (
									<TableRow hover role="checkbox" tabIndex={-1} key={row.name}>
										{columns.map((column) => {
											const col_id = column.id
											let value = row[col_id];
											if(col_id === 'created' || col_id === 'updated') {
												value = dateFormatToLocalString(row[col_id])
											}
											return (
												<TableCell key={column.id} align={column.align}>
													{col_id === 'actions' && 
														<Box display="flex" flexDirection="row">
															<Button
																color="info"
																variant="outlined"
																size="small"
																style={{marginRight: '8px'}}
																onClick={()=> props.handleSelectedOrder(row)}
															>
																Edit
															</Button>
															<Button
																color="error"
																variant="outlined"
																size="small"
																onClick={()=> handleDeleteProduct(row.id)}
															>
																Delete
															</Button>
														</Box>
													}
													{col_id !== 'actions' && column.format && typeof value === 'number'
														? column.format(value)
														: value}
												</TableCell>
											);
										})}
									</TableRow>
								);
							})}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				rowsPerPageOptions={[7, 21, 100]}
				component="div"
				count={rows.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={handleChangePage}
				onRowsPerPageChange={handleChangeRowsPerPage}
			/>
		</Paper>
	);
}
